import React, { useState, useContext, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { fileHandler, mapRowData } from 'utils/excelRenderer';
import CasesAPI from 'api/CasesAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import AutomationAPI from 'api/AutomationAPI';
import pako from 'pako';
import { format } from 'date-fns';
import { Buffer } from 'buffer';
import { convertBase64ToFile } from 'utils/fileHelper';

interface IModalCommon {
  handleClose?: any;
  cur_case: any;
  handleTabSwitch: any;
}

interface value {
  searchVal: string;
  type: string;
}
export interface automationParams {
  values?: value[];
  entState?: IEntityState;
  comState?: ICommodityState;
  row?: string;
}

interface dataObj {
  type: string;
  searchVal: string;
  row: number;
}

interface IEntityState {
  fuzzy: boolean;
  dateRange: string[];
  lenses: string[];
  countries: string[];
  agencies: string[];
  lists: string[];
}

interface ICommodityState {
  fuzzy: boolean;
  dateRange: string[];
  comLenses: string[];
  countries: string[];
  agencies: string[];
  lists: string[];
}

const SupQuestSubmit: React.FC<IModalCommon> = ({
  handleClose,
  cur_case,
  handleTabSwitch,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { cur_user } = useContext(GlobalContext);
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any>([]);
  const [rowData, setRowData] = useState([]);

  const reloadPage = () => {
    document.body.style.opacity = '0.5'; // Fade effect
    setTimeout(() => window.location.reload(), 3000);
  };

  type Item = {
    type: string;
    searchVal: string;
    row?: number;
  };

  type InputArray = Item[][];

  type OutputArray = Item[][];

  const deduplicateObjects = (input: InputArray): OutputArray => {
    const seen = new Set<string>();

    return input.map((subArray) => {
      return subArray.filter((item) => {
        const key = `${item.type}-${item.searchVal}`;
        if (!seen.has(key)) {
          seen.add(key);
          return true;
        }
        return false;
      });
    });
  };

  const compressData = (data: any) => {
    const stringifiedData = JSON.stringify(data);
    const compressed = pako.gzip(stringifiedData);
    return Buffer.from(compressed).toString('base64'); // Convert to base64 for storage or transmission
  };

  const autoAPICall = async (rowsArr: any) => {
    let allResults: any = [];
    let resCount = 0;
    let totalResults = 0;

    const findMatchingIndex = (val: string, arr: any[]): number | false => {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i].field === val) {
          return i;
        }
      }
      return false;
    };

    for (const [i, row] of rowsArr.entries()) {
      const query: automationParams = {
        values: row,
        entState: {
          lenses: cur_case.entityFilter.lenses,
          countries: cur_case.entityFilter.countries,
          agencies: cur_case.entityFilter.agencies,
          lists: cur_case.entityFilter.lists,
          fuzzy: cur_case.entityFilter.entRes,
          dateRange: cur_case.entityFilter.filterDate,
        },

        comState: {
          comLenses: cur_case.commodityFilter.lenses,
          countries: cur_case.commodityFilter.countries,
          agencies: cur_case.commodityFilter.agencies,
          lists: cur_case.commodityFilter.lists,
          fuzzy: cur_case.commodityFilter.entRes,
          dateRange: cur_case.commodityFilter.filterDate,
        },
        row: i + 1,
      };

      const res = await AutomationAPI.automationDataSearch(query);
      resCount++;

      if (res?.success) {
        if (res?.result[0].result?.body?.hits?.total?.value) {
          totalResults =
            totalResults + res?.result[0].result?.body?.hits?.total?.value;
        }

        if (res?.result[1].result?.body?.hits?.total?.value) {
          totalResults =
            totalResults + res?.result[1].result?.body?.hits?.total?.value;
        }

        const entityResArr: any[] = [];
        const commodityResArr: any[] = [];

        await res?.result[0].result?.body?.hits?.hits?.forEach((hit: any) => {
          const val = hit.matched_queries ? hit.matched_queries[0] : '';
          const i = findMatchingIndex(val, entityResArr);
          if (i !== false) {
            entityResArr[i].hits++;
            entityResArr[i].docs.push(hit._source);
          } else {
            entityResArr.push({
              field: val,
              row: res.result[0].row,
              dataSet: 'Entites',
              hits: 1,
              docs: [hit._source],
            });
          }
        });
        await res?.result[1].result?.body?.hits?.hits?.forEach((hit: any) => {
          const val = hit.matched_queries ? hit.matched_queries[0] : '';
          const i = findMatchingIndex(val, commodityResArr);
          if (i !== false) {
            commodityResArr[i].hits++;
            commodityResArr[i].docs.push(hit._source);
          } else {
            commodityResArr.push({
              field: val,
              row: res.result[1].row,
              dataSet: 'Commodities',
              hits: 1,
              docs: [hit._source],
            });
          }
        });

        allResults = [...allResults, ...entityResArr, ...commodityResArr];

        if (resCount === rowsArr.length) {
          return {
            all: [...allResults],
            tot: totalResults,
          };
        }
      } else {
        const error_msg =
          'Error screening data. An API call has failed or timed out.';
        enqueueSnackbar(error_msg, { variant: 'error' });
        return {
          all: [],
          tot: 0,
        };
      }
    }
  };

  const handleTicket = async (values: any) => {
    if (cur_case) {
      const update = {
        _id: cur_case._id,
        ...values.basics,
        ...values,
      };
      const resp = await CasesAPI.updateCase(update);

      if (resp?.success) {
        enqueueSnackbar('Successfully updated case', { variant: 'success' });
        setLoading(false);
        handleTabSwitch('Results');
        handleClose();
        reloadPage();
      } else if (resp) {
        const error_msg =
          'Error updating case. Uploaded file(s) may be too large.';
        enqueueSnackbar(error_msg, { variant: 'error' });
        handleClose();
      }
    }
  };

  const screenFile = async () => {
    setLoading(true);
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: 'screened the uploaded supplier questionnaire',
      timeStamp: Date.now(),
    };

    const query = {
      status: 'Complete',
      lastUpdate: formatted_dt,
      activity: [...cur_case.activity, actObj],
    };

    if (rowData.length > 0) {
      const updatedRows: any[] = [];
      rowData.map((obj: dataObj) => {
        if (obj.searchVal !== ' ') {
          const i = obj.row;
          if (i > updatedRows.length - 1) {
            updatedRows.push([obj]);
          } else {
            updatedRows[i].push(obj);
          }
        }
      });

      const uniqueRows = deduplicateObjects(updatedRows);

      const obj = await autoAPICall(uniqueRows);
      if (obj) {
        const ticketQuery = {
          ...query,
          curTotal: obj.tot,
          curNew: 0,
          results: {
            allResults: compressData(obj.all),
            allTotal: obj.tot,
            newResults: [],
            newTotal: 0,
            date: formatted_dt,
            docInfo: uniqueRows,
          },
        };
        handleTicket(ticketQuery);
      }
    } else {
      enqueueSnackbar('No data to screen', { variant: 'success' });
      handleClose();
    }
  };

  const entityHeaders = [
    'Importer of Record Name',
    'Importer of Record',
    'Exporter Name',
    'Buyer Name',
    'Seller Name',
    'Manufacturer Name',
    'Ship To Party',
    'Consolidator (Stuffer) Name',
    'Vessel Name',
    "Carrier's Information",
    'Company Legal Name',
    'Parent Company (If applicable)',
    'Company Identification Number',
    'Contact Person Information',
    'Company Website',
    'Zone Approval Officer',
    'Vessel Name or Aircraft',
    'Name of Facility',
    'Contact Name',
    'Parent Company',
    'Facility Owners',
    'Subcontractors (If Applicable)',
    'Customs Broker Name',
    'Carrier Contact Name',
    'Carrier Name',
    'Consignee Contact Name',
    'Consignee Company Name',
    'Consignee Address',
    'Shipper Email',
    'Shipper Contact Name',
    'Shipper Company Name',
    'Shipper Address',
  ];
  const codeHeaders = [
    'Commodity Harmonized Tariff Schedule Number (HTS)',
    'HS Code',
    'HS Code(s)',
    'Item',
  ];
  const descHeaders = [
    'Product Detailed Information',
    'Description of Merchandise',
    'Item',
    'Assembly Name or Number',
    'Sub Assembly Name or Number',
    'Component / Part Name',
    'Part Number',
    'Description',
    'Description of Goods',
  ];
  const countryHeaders = ['Country of Origin'];

  const mapType = (header: string) => {
    if (entityHeaders.includes(header)) {
      return 'entity';
    } else if (codeHeaders.includes(header)) {
      return 'code';
    } else if (descHeaders.includes(header)) {
      return 'desc';
    } else if (countryHeaders.includes(header)) {
      return 'country';
    }
    return 'bulk';
  };

  const handleFile = async () => {
    const fileType = convertBase64ToFile(
      cur_case.documents[0].uploadedFile.base64,
      cur_case.documents[0].uploadedFile.name,
    );

    const { rows } = await fileHandler(fileType);
    const colNames: string[] = rows[0];

    const rowData = rows.slice(1);
    const mappedRowData = mapRowData(colNames, rowData);
    setRows(mappedRowData);
  };

  useEffect(() => {
    handleFile();
  }, []);

  const valsArrofObjs: any = [];

  useEffect(() => {
    if (rows.length > 0) {
      rows.forEach((row: any, i: number) => {
        Object.keys(row).forEach((key) => {
          const obj = {
            type: mapType(key),
            searchVal: row[key],
            row: i,
          };
          if (obj.type) {
            valsArrofObjs.push(obj);
          }
        });
      });
      setRowData(valsArrofObjs);
    } else {
      setRowData([]);
    }
  }, [rows]);

  return (
    <>
      {loading ? (
        <div className='audit-add-modal-cont'>
          <div className='loading-spinner-cont-modal'>
            <svg
              className='spinner'
              xmlns='http://www.w3.org/2000/svg'
              width='32'
              height='33'
              viewBox='0 0 32 33'
              fill='none'
            >
              <g clipPath='url(#clip0_4006_13811)'>
                <circle
                  cx='16'
                  cy='3.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.1'
                />
                <circle
                  opacity='0.45'
                  cx='16'
                  cy='29.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.65'
                />
                <circle
                  cx='3'
                  cy='16.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.85'
                />
                <circle
                  cx='29'
                  cy='16.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.35'
                />
                <circle
                  cx='26'
                  cy='7.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.25'
                />
                <circle
                  cx='26'
                  cy='25.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.45'
                />
                <circle
                  cx='6'
                  cy='25.75'
                  r='3'
                  fill='white'
                  fillOpacity='0.75'
                />
                <circle cx='6' cy='7.75' r='3' fill='white' />
              </g>
              <defs>
                <clipPath id='clip0_4006_13811'>
                  <rect
                    width='32'
                    height='32'
                    fill='white'
                    transform='translate(0 0.75)'
                  />
                </clipPath>
              </defs>
            </svg>
            <p style={{ color: 'var(--alternative-text-color)' }}>Screening in Progress...</p>
          </div>
        </div>
      ) : (
        <div className='audit-add-modal-cont'>
          <div className='audit-add-modal-cont-head'>
            <div className='audit-add-modal-head-cont-change'>
              <div className='audit-add-modal-head-cont-icon'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M8 8V7M8 12.5V11.5M8 17V16M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z'
                    stroke='#4895EF'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='audit-add-modal-head-cont-supporting'>
                <h1>Screen Questionnaire</h1>
                <p>
                  Your supplier has uploaded answers to the questionnaire,
                  please click the button below to screen the document.
                </p>
              </div>
            </div>
            <div className='audit-add-modal-head-close' onClick={handleClose}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M18 6L6 18M6 6L18 18'
                  stroke='#85888E'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
          <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
          <div className='audit-modal-cont-footer'>
            <div className='audit-modal-cont-footer-content'>
              <div
                className='audit-modal-cont-footer-content-btn-cancel'
                onClick={handleClose}
              >
                <div className='audit-modal-cont-footer-content-btn-text'>
                  Cancel
                </div>
              </div>
              <div
                className='audit-modal-cont-footer-content-btn-upload'
                onClick={() => {
                  screenFile();
                }}
              >
                <div className='audit-modal-cont-footer-content-btn-text'>
                  Screen
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupQuestSubmit;
